/**
 * Help Center: "flight-and-hotel" section articles. Format and rules: src/content/help/README.md.
 */
export default [
  {
    slug: 'book-flight-and-hotel',
    title: 'Booking a flight and hotel together',
    summary: 'Tap Stays in the search toggle, pick your flights, then choose a hotel and room. Your hotel dates match your flights, and it’s all one trip on one payment plan.',
    keywords: ['Stays', 'vacation package', 'hotel package', 'lodging', 'book a hotel', 'bundle', 'hotel only', 'Flight + Hotel'],
    related: [
      'flight-and-hotel/hotel-rates-and-terms',
      'getting-started/defera-installments',
      'flight-and-hotel/hotel-information',
      'flight-and-hotel/hotel-changes-cancellations',
    ],
    updated: '2026-10-05',
    body: `You can book a flight and hotel together as one Defera trip, called a Flight + Hotel package. Tap **Stays** in the search toggle, pick your flights first, then choose a hotel and room in your destination city.

Your hotel dates match your flights automatically, and you pay for the whole package with Defera Installments, our pay-over-time plan. That means one trip, with one deposit and one payment schedule.

## How do I book a Flight + Hotel package?

Here's how it works:

1. **Start a Stays search.** On **Home** or in [Book](/search), tap **Stays** in the toggle at the top of the search (Flights · Stays · Explore).
2. **Search for flights as usual.** Enter where you're flying from and to, your dates, your travelers and your cabin.
3. **Pick your flights.** Choose your outbound flight, and your return flight if it's a round trip.
4. **Choose a hotel.** You land on the Stays page for your destination city. Compare hotels by price or rating, search the list, or switch to the map view.
5. **Pick a room.** Open **Room Rates** for a hotel and choose a specific room.
6. **Check out.** Your flight and hotel are booked together as one Defera trip.

## How are my hotel dates set?

Your hotel dates are set from your flights, so you don't need to enter them. Check-in is set from your outbound flight's arrival, and check-out from your return flight's departure.

For example, if your outbound flight lands on June 10 and your return flight leaves on June 15, your hotel stay runs from June 10 to June 15.

## How do I pay for a package?

Flight + Hotel packages are paid with Defera Installments: a deposit when you book, then automatic weekly or bi-weekly payments.

- **One plan for everything.** Your deposit and payments cover the flight and hotel together.
- **Your deposit.** Deposit options depend on how far away your trip is, and they appear at checkout. They start as low as 5% on trips more than 90 days away.
- **No credit check and no interest.** It works the same way as on a flight-only trip.
- **Price protection.** Your trip price is secured once your booking is approved, and it stays protected while you're in good standing, which means keeping up with your payment plan.

Defera Zero, our plan with nothing down at booking, isn't available on Flight + Hotel packages. A package uses Defera Installments, with a deposit for the combined flight and hotel total.

## Can I use Defera Miles on a package?

Yes. You can apply Defera Miles at checkout on a package, the same as on any Defera Installments trip. A package can't be booked with Miles alone, though, so part of it always stays on your payment plan. [Using your Miles at checkout](/help/rewards/using-miles-at-checkout) explains how.

## Can I book a hotel without a flight?

Not on its own. Every package starts with a flight, so there are no hotel-only bookings on Defera.

## Can I add a hotel to a flight I've already booked?

Not to that booking. A package's flight and hotel are booked together, so a hotel can't be added to a flight you've already booked. Searching with **Stays** starts a new, separate trip, with its own flight and its own payment plan.

If you'd like a hotel for a trip you've already booked, ask the [Defera Concierge](/support), our AI concierge, which can send a request to our team.

## Will I pay anything at the hotel?

The trip total you see at checkout is the full price, with nothing added later. The one exception: some packages carry fees the hotel collects at check-in. If yours does, those fees are labeled on your trip, so you'll know what to expect when you arrive.

## Do I leave Defera to book the hotel?

No. Everything happens inside Defera, from search and room choice to checkout and your payment schedule.`,
  },
  {
    slug: 'hotel-information',
    title: 'Where’s my hotel information?',
    summary: 'Your Flight + Hotel package is one Defera trip, so you’ll find it in Trips. Your hotel dates follow your flights, and any fees due at the hotel are labeled there.',
    keywords: ['stay details', 'my hotel booking', 'Stays', 'check-in date', 'check-out date', 'hotel fees', 'package trip'],
    related: [
      'flight-and-hotel/hotel-confirmation',
      'flight-and-hotel/hotel-changes-cancellations',
      'payments/view-payment-schedule',
      'contact/how-requests-work',
    ],
    updated: '2026-10-05',
    body: `Your flight and hotel were booked together as one Defera trip, so you'll find your Flight + Hotel package in **Trips**, like any other trip. Open **Trips** and tap your Flight + Hotel trip.

Your hotel dates follow your flights, and any fees the hotel collects at check-in are labeled on your trip. If you need a detail about your stay that you can't find, the Defera Concierge can help.

## How were my hotel dates set?

Your hotel dates were set from your flights when you booked:

- **Check-in** is set from your outbound flight's arrival.
- **Check-out** is set from your return flight's departure.

For example, if your outbound flight lands on June 10 and your return flight leaves on June 15, your stay runs from June 10 to June 15.

If your flights change after you book, ask the Defera Concierge how that affects your stay. [Changing or cancelling a hotel stay](/help/flight-and-hotel/hotel-changes-cancellations) explains how to ask.

## Will I owe anything at the hotel?

Your trip total is the full price of your package, with nothing added later. The one exception: some packages carry fees the hotel collects at check-in. If yours does, those fees are labeled on your trip, so you know what to expect when you arrive.

## Why is there only one payment schedule?

Your deposit and payments cover the flight and hotel together, so the payment schedule on your trip is for the whole package. You won't see one schedule for the flight and another for the hotel.

The same goes for your airline confirmation number. It's released once your trip is paid in full, and because your plan covers both, paid in full means the whole package. [When do I get my hotel confirmation?](/help/flight-and-hotel/hotel-confirmation) explains what you get and when.

## Can't find what you need?

[Chat with the Defera Concierge](/support), our AI concierge. It can check on your trip and send a request to our team. Include:

- your Defera booking reference, the code at the top of your booking confirmation email
- the hotel's name and your stay dates
- what you're looking for

A request shows as a card on your trip page, and the answer arrives there and by email. You can also [contact us](/contact).`,
  },
  {
    slug: 'hotel-confirmation',
    title: 'When do I get my hotel confirmation?',
    summary: 'Your booking confirmation email comes the moment you book. For your hotel’s own confirmation details, ask the Defera Concierge to send a request to our team.',
    keywords: ['reservation number', 'proof of accommodation', 'hotel booking reference', 'Stays', 'package', 'booking confirmation email', 'hotel proof'],
    related: [
      'flight-and-hotel/hotel-information',
      'confirmation-and-travel/when-confirmation-number',
      'contact/how-requests-work',
      'emails/booking-confirmation-email',
    ],
    updated: '2026-10-23',
    body: `When you book a Flight + Hotel package, you get a booking confirmation email the moment you book, and your trip appears in **Trips**. For your hotel's own confirmation details, chat with the Defera Concierge, our AI concierge. It can send a request to our team, and the answer comes to your trip page and your email.

Your hotel is part of your Defera trip from the moment you book, so there's no separate hotel booking for you to make.

## What do I get when I book?

- **A booking confirmation email,** the moment you book, with your Defera booking reference at the top. If you can't find it, everything in it is also on your trip page.
- **Your trip in Trips,** with one payment schedule for your flight and hotel together.

## Which code is which?

A few different codes can come up with a package:

- **Your Defera booking reference** is the code at the top of your booking confirmation email. It's how we find your trip at Defera.
- **Your airline confirmation number** is the code the airline uses to find your flight reservation. It's released once your trip is paid in full.
- **A hotel confirmation number** is the code a hotel uses to find a reservation. For your hotel's confirmation details, ask the Defera Concierge.

## When do I get my airline confirmation number?

Your airline confirmation number is released once your trip is paid in full. Your payment plan covers the flight and hotel together, so paid in full means the whole package. [When do I get my confirmation number?](/help/confirmation-and-travel/when-confirmation-number) explains the timing.

## How do I ask for my hotel confirmation details?

Here's what happens:

1. **You ask the Defera Concierge.** [Chat with the Defera Concierge](/support), our AI concierge, and tell it what you need. It can send a request to our team.
2. **You share the details.** Include your Defera booking reference, the hotel's name, your stay dates, and what you need the details for, such as proof of where you're staying.
3. **Our team works on it.** The request shows as a card on your trip page, marked "Submitted · in progress" while our team works on it.
4. **You get the answer.** It appears on that card, and it comes to you by email too.

## Other ways to reach us

You can also reach the same concierge by phone, 24/7, at [{{SUPPORT_PHONE}}](tel:{{SUPPORT_PHONE_TEL}}), or send us a message from the [Contact page](/contact#message). Our team replies within 24 hours.`,
  },
  {
    slug: 'hotel-changes-cancellations',
    title: 'Changing or cancelling a hotel stay',
    summary: 'Your flight and hotel are one trip on one payment plan. To change or cancel your stay, ask the Defera Concierge, and it can send a request to our team.',
    keywords: ['cancel hotel', 'change hotel', 'different room', 'hotel dates', 'add a hotel later', 'Stays', 'cancel package'],
    related: [
      'flight-and-hotel/book-flight-and-hotel',
      'changes-cancellations/cancel-within-24-hours',
      'changes-cancellations/cancel-your-trip',
      'contact/how-requests-work',
    ],
    updated: '2026-10-23',
    body: `Your flight and hotel were booked together as one Defera trip, on one payment plan. To change or cancel the hotel part of your trip, chat with the Defera Concierge, our AI concierge, or contact us. The Defera Concierge can send a request to our team, and our team reviews it.

We know plans change. If you're changing your flights, you can also ask how that affects your stay.

## How do I change or cancel my stay?

Ask us about a different room, a different hotel or different dates, or about cancelling your stay. [Chat with the Defera Concierge](/support), and it can send a request to our team. Include:

- your Defera booking reference, the code at the top of your booking confirmation email
- the hotel and your stay dates
- what you'd like to change

Our team reviews each request. It shows as a card on your trip page: "Submitted · in progress" while our team works on it, then the answer. You'll get the answer by email too.

## How do I cancel my whole trip?

To cancel your flight and hotel together, open your trip in **Trips**, tap **Flight Options**, then **Cancel**. The screen shows the cancellation options for your trip before you choose one.

If you're not sure how cancelling affects your hotel, ask the Defera Concierge before you confirm. [How to cancel your trip](/help/changes-cancellations/cancel-your-trip) has more.

## What if I cancel within 24 hours of booking?

If you cancel within 24 hours of booking, your deposit is refunded in full to the card you paid with, automatically. This applies on every payment plan.

For example, if you book on a Monday at noon and cancel before Tuesday at noon, your deposit comes back in full. Most of the time, the deposit charge then disappears from your statement within a day or so. If it had already posted, the money returns to your card within a few business days. [Cancelling within 24 hours of booking](/help/changes-cancellations/cancel-within-24-hours) has the details.

## Can I add a hotel to a trip I've already booked?

A hotel can't be added to a flight you've already booked, because a package's flight and hotel are booked together. Searching with **Stays** starts a new, separate trip, with its own flight and its own payment plan, so it won't add a hotel to the trip you have.

If you'd like a hotel for a trip you've already booked, ask the Defera Concierge, which can send a request to our team.

## Other ways to reach us

Call [{{SUPPORT_PHONE}}](tel:{{SUPPORT_PHONE_TEL}}) to reach the same concierge by voice, 24/7, or send us a message from the [Contact page](/contact#message). Our team replies within 24 hours.`,
  },
  {
    slug: 'hotel-rates-and-terms',
    title: 'Hotel rooms, rates and terms',
    summary: 'Compare hotels on the Stays page, then open Room Rates to pick your room. Your trip total is the full price, apart from any fees the hotel collects at check-in.',
    keywords: ['room types', 'room price', 'Room Rates', 'hotel fees', 'resort fee', 'hotel policy', 'Stays', 'choose a room'],
    related: [
      'flight-and-hotel/book-flight-and-hotel',
      'getting-started/what-you-pay',
      'booking/checkout-page',
      'flight-and-hotel/hotel-changes-cancellations',
    ],
    updated: '2026-10-05',
    body: `On the Stays page, you compare hotels in your destination city, then open **Room Rates** for a hotel to pick a specific room. When you choose a hotel and room, your flight and hotel are checked out together as one Defera trip.

Your checkout page shows your trip total before you book, so you'll see what the whole package costs before you commit. If you have a question about a room before you book, the Defera Concierge can help.

## How do I compare hotels?

The Stays page lists hotels in your destination city. To narrow them down, you can:

- sort by price, from high to low or low to high, or by rating
- search the list
- switch between the list and the map

## How do I choose a room?

Each hotel has a **Room Rates** screen, where you choose a specific room. Your hotel dates are already set from your flights: check-in from your outbound flight's arrival, and check-out from your return flight's departure.

## What will I see at checkout?

Before you book, your checkout page shows:

- what's due today
- your payments: how many there are, how much each one is, and the date of the last one
- your trip total, including taxes and fees

There's also a **Price Breakdown** of the costs, open by default. Each fee has a small info icon that explains what it's for.

## Are there any extra costs?

Your trip total covers the flight and hotel together, and your deposit and payments on Defera Installments are spread across that combined total. It's the full price, with nothing added later.

The one exception: some packages carry fees the hotel collects at check-in. If yours does, those fees are labeled on your trip, so you'll know what to expect when you arrive.

## What are the room's terms?

If you want to know more about a room before you book, such as what's included or what happens if your plans change, ask the [Defera Concierge](/support), our AI concierge, or [contact us](/contact) before you check out. That way, you'll know what you're booking before you pay.

## Can I change my room after booking?

Ask the Defera Concierge about a different room. It can send a request to our team, and our team reviews it. [Changing or cancelling a hotel stay](/help/flight-and-hotel/hotel-changes-cancellations) explains how it works.`,
  },
];
